import { CurrencyPipe, DatePipe, TitleCasePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import {Agenzia} from '../../pages/store-locator-page/interfaces/agenzia.interface';
import {IRemoteProfile} from '../interfaces/iremote-profile';
import {Networks} from '../models/networks.enum';
import {CruiseTopId} from '../models/cruise-top-id.enum';

@Injectable({
  providedIn: 'root'
})
export class DemService {
  private readonly templatesDir = 'assets/templates';
  private httpCache = new Map<string, Observable<any>>();
  private designSystem = 'https://lamp-dev.offertetouroperator.com/projects/zeus/zeus-design-system/styles/partners/oto/assets/e-icons/';
  private layouts = {
    holiday: `${this.templatesDir}/holidayTemplate.html`,
    holidayWTG: `${this.templatesDir}/holidayTemplateWTG.html`,
    cruise: `${this.templatesDir}/cruiseTemplate.html`,
    cruiseWTG: `${this.templatesDir}/cruiseTemplateWTG.html`,
    wtgCatalogMail: `${this.templatesDir}/wtg_catalog_email.html`,
    geoCatalogMail: `${this.templatesDir}/geo_catalog_email.html`
  }
  private readonly categorySymbols = {
    STAR: '⭐',
    SPIKE: '🌾',
    KEY: '🔑'
  };

  private demType = null;
  private dataToInsert = null;
  private agencyNetwork = null;

  constructor(
    private _http: HttpClient,
    private _datePipe: DatePipe,
    private _currencyPipe: CurrencyPipe,
    private _titlecasePipe: TitleCasePipe,
  ) {
  }

  getLayout(layoutName: string) {
    if (!this.httpCache.get(this.layouts[layoutName])) {
      this.httpCache.set(this.layouts[layoutName], this._http.get(this.layouts[layoutName], {responseType: 'text'}).pipe(
        shareReplay({bufferSize: 1, refCount: true})
      ));
    }

    return this.httpCache.get(this.layouts[layoutName]).toPromise()
  }

  formatDem(html: string, dataToInsert: any, typology: 'holiday' | 'cruise', action?: string, agencyNetwork: any = null, isProfileDataFromStoreList: boolean = false) {
    this.agencyNetwork = agencyNetwork;
    this.demType = action;
    if (isProfileDataFromStoreList && dataToInsert?.profileData) {
      // se è stato passato il profileData, procedo a formattarlo seguendo il formato della dem (che utilizza l'interfaccia IRemoteProfile)
      dataToInsert.profileData = this.formatProfileDataForDem(dataToInsert.profileData);
    }
    this.dataToInsert = dataToInsert;
    this.dataToInsert.designSystem = this.designSystem;
    if (!this.dataToInsert.currentQuote?.packageContent?.demCoverImage && this.dataToInsert.shipService?.images?.length > 0) {
      this.dataToInsert.currentQuote.packageContent.demCoverImage = this.dataToInsert.shipService?.images[0]?.image_path;
    }

    // se manca l'oggetto con le info del tour operator, provvedo a recuperarlo dal "details"
    if (!this.dataToInsert.currentQuote.tourOperator) {
      this.dataToInsert.currentQuote.tourOperator = this.dataToInsert.details.tourOperator;
    }

    const layoutDOM = new DOMParser().parseFromString(this.replacePlaceholders(html), 'text/html');

    const itineraryNameElement = layoutDOM.querySelector('#itineraryName');
    if (itineraryNameElement) {
      const originalText = itineraryNameElement.textContent || '';
      const newText = originalText.toString().replace(/,/g, ', ');
      itineraryNameElement.textContent = newText;
    }
    if (typology === 'holiday') {
      return this.formatHolidayDem(layoutDOM);
    } else if (typology === 'cruise') {
      return this.formatCruiseDem(layoutDOM);
    }
  }

  formatHolidayDem(layoutDOM) {
    return new Promise((resolve, reject) => {
      if (this.dataToInsert.currentQuote?.related?.length > 0) {
        this.insertRelated(layoutDOM);
      } else {
        layoutDOM.getElementById('relatedToRepeat')?.remove();
      }
      this.insertCharges(layoutDOM);
      this.insertMeetingPoints(layoutDOM);
      this.insertAgencyMessage(layoutDOM);
      this.insertTourOrAccommodationDescription(layoutDOM);
      this.insertPassengersData(layoutDOM);
      this.insertContactInfo(layoutDOM);
      this.cleanDEM(layoutDOM);

      resolve({
        title: this.demType + ' ' + layoutDOM.getElementById('emailTitleText')?.innerText,
        body: layoutDOM.documentElement.outerHTML,
        loggedEmail: this.dataToInsert?.profileData?.email_1,
        ccnEmail : (this.dataToInsert?.profileData?.email_2 === 'prenotazioni.alltrade@gmail.com') ? this.dataToInsert?.profileData?.email_2 : null,
        masterEmail: this.dataToInsert?.masterProfileData?.email_1,
        customerEmail: this.dataToInsert?.customerForm?.quotation?.camere[0].passeggeri[0]?.email,
        customerZeusEmail: this.dataToInsert.currentQuote?.customer?.find(customer => customer.first === 1)?.email
      });
    });
  }

  formatCruiseDem(layoutDOM: any) {
    return new Promise((resolve, reject) => {
      if (this.dataToInsert.currentQuote?.related?.length > 0) {
        this.insertRelated(layoutDOM);
      } else {
        layoutDOM.getElementById('relatedToRepeat')?.remove();
      }
      this.insertCruiseCharges(layoutDOM);
      this.insertMeetingPoints(layoutDOM);
      this.insertAgencyMessage(layoutDOM);
      this.insertPassengersData(layoutDOM);
      this.insertContactInfo(layoutDOM);
      this.insertExpireDate(layoutDOM);
      this.cleanDEM(layoutDOM);

      resolve({
        title: this.demType + layoutDOM.getElementById('emailTitleText')?.innerText,
        body: layoutDOM.documentElement.outerHTML,
        loggedEmail: this.dataToInsert?.profileData?.email_1,
        ccnEmail : (this.dataToInsert?.profileData?.email_2 === 'prenotazioni.alltrade@gmail.com') ? this.dataToInsert?.profileData?.email_2 : null,
        masterEmail: this.dataToInsert?.masterProfileData?.email_1,
        customerEmail: this.dataToInsert?.customerForm?.quotation?.camere?.passeggeri[0]?.email,
        customerZeusEmail: this.dataToInsert.currentQuote?.customer?.find((customer: any) => customer.first === 1)?.email
      });
    });
  }

  replacePlaceholders(layoutHtml: string) {
    const regexpBrackets = new RegExp(/({{)(.*?)(}})/g);
    const placeholders = layoutHtml.match(regexpBrackets);

    if (placeholders && placeholders.length > 0) {
      placeholders.forEach((placeholder: any) => {
        const placeholderSanitized = placeholder.replace(new RegExp(/[{{]|[}}]/g), '');
        layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, this.getDataToInsertFromPath(placeholderSanitized) || '');
      });
    }
    return layoutHtml;
  }

  replaceShareEmail(layoutHtml: string, templateData : TemplateData) : string {
    const regexpBrackets = new RegExp(/({{)(.*?)(}})/g);
    const placeholders = layoutHtml.match(regexpBrackets);

    if (placeholders && placeholders.length > 0) {
      placeholders.forEach((placeholder: any) => {
        const placeholderSanitized = placeholder.replace(new RegExp(/[{{]|[}}]/g), '');
        if(placeholderSanitized === "title") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.ogTitle);
        if(placeholderSanitized === "subtitle") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.ogDescription);
        if(placeholderSanitized === "img") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.ogImg);
        if(placeholderSanitized === "link") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.link);
        if(placeholderSanitized === "logoLink") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.logoLink);
        if(placeholderSanitized === "address") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.address);
        if(placeholderSanitized === "email") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.email);
        if(placeholderSanitized === "city") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.city);
        if(placeholderSanitized === "agencyName") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.naming);
      });
    }
    return layoutHtml;
  }

  getDataForShareCatalog(path: string) {

  }

  getShipName(): string {
    return this.dataToInsert.currentQuote?.packageContent?.shipName || 'n/a';
  }

  getExternalBookingCode() {
    return this.dataToInsert.currentQuote?.bookingCode || '';
  }

  getDeparturePlaceName() {
    return this.dataToInsert.currentQuote?.operatives?.[0]?.departurePlaceName ?? `${this.dataToInsert?.currentQuote?.packageContent?.departurePlace?.code}${this.dataToInsert?.currentQuote?.packageContent?.departurePlace?.code ? ' - ' : ''}${this.dataToInsert?.currentQuote?.packageContent?.departurePlace?.name || ''}`;
  }

  getStopOverPlaceName() {
    return (this.dataToInsert.currentQuote?.operatives?.[0]?.stopOver) ? `${this.dataToInsert.currentQuote.operatives[0].stopOver.code}${this.dataToInsert.currentQuote.operatives[0].stopOver.code ? ' - ' : ''}${this.dataToInsert.currentQuote.operatives[0].stopOver.name || ''}` : '';
  }

  getArrivalPlaceName() {
    return this.dataToInsert.currentQuote?.operatives?.[0]?.arrivalPlaceName ?? `${this.dataToInsert?.currentQuote?.packageContent?.arrivalPlace?.code}${this.dataToInsert?.currentQuote?.packageContent?.arrivalPlace?.code ? ' - ' : ''}${this.dataToInsert?.currentQuote?.packageContent?.arrivalPlace?.name || ''}`;
  }

  getDataToInsertFromPath(path: string) {
    if (path === 'currentQuote.createdAt') {
      return this._datePipe.transform(this.dataToInsert.currentQuote?.createdAt, 'dd/MM/yyyy HH:mm');
    }
    if(path === 'currentQuote.components[0].city'){
      return this.dataToInsert.currentQuote?.components[0]?.city;
    }
    if (path === 'currentQuote.packageContent.demCoverImage') {
      return this.dataToInsert.currentQuote?.packageContent?.demCoverImage || '';
    }
    if (path === 'currentQuote.packageContent.departureDateFrom') {
      return this._datePipe.transform(this.dataToInsert.currentQuote?.packageContent?.departureDateFrom, 'dd/MM/yyyy');
    }
    if (path === 'currentQuote.packageContent.departureDateTo') {
      return this._datePipe.transform(this.dataToInsert.currentQuote?.packageContent?.departureDateTo, 'dd/MM/yyyy');
    }
    if (path === 'currentQuote.components[0].description') {
      return this.dataToInsert.currentQuote?.components[0]?.description;
    }
    if (path === 'currentQuote.cabins[0].description') {
      return this.dataToInsert.currentQuote?.cabins[0]?.description;
    }
    if (path === 'currentQuote.cabins[0].cabinNumber') {
      return this.dataToInsert.currentQuote?.cabins[0]?.cabinNumber;
    }
    if (path === 'currentQuote.cabins[0].sourceId') {
      return this.dataToInsert.currentQuote?.cabins[0]?.sourceId;
    }
    if (path === 'currentQuote.customer[0].surname') {
      return this.dataToInsert.currentQuote?.customer?.find(customer => customer.first === 1)?.surname;
    }
    if (path === 'currentQuote.customer[0].name') {
      return this.dataToInsert.currentQuote?.customer?.find(customer => customer.first === 1)?.name;
    }
    if (path === 'currentQuote.selectedLiveSlim.consumerPrice') {
      return (this.dataToInsert.currentQuote?.selectedLiveSlim?.consumerPrice) ? Number(this.dataToInsert.currentQuote?.selectedLiveSlim?.consumerPrice) : '';
    }
    if (path === 'currentQuote.packageContent.packageDetail.category.typology') {
      let toReturn = '';
      if (this.dataToInsert.currentQuote?.packageContent?.packageDetail?.category?.description?.includes('/')) {
        toReturn = this.categorySymbols[this.dataToInsert.currentQuote?.packageContent?.packageDetail?.category?.typology];
      } else {
        for (let i = 0; i < Number(this.dataToInsert.currentQuote?.packageContent?.packageDetail?.category?.description); i++) {
          toReturn += this.categorySymbols[this.dataToInsert.currentQuote?.packageContent?.packageDetail?.category?.typology];
        }
      }
      return toReturn;
    }

    if (path.includes('()')) {
      // tslint:disable-next-line:no-eval
      return eval(path);
    }

    return path.split('.').reduce((prev: { [x: string]: any; }, curr: string | number) => {
      return prev ? prev[curr] : '';
    }, this.dataToInsert || self);
  }

  cleanDEM(layoutDOM: Document) {
    const flightFrom = layoutDOM.querySelectorAll('.flightFrom');
    const flightTo = layoutDOM.querySelectorAll('.flightTo');
    const flightBoth = layoutDOM.querySelectorAll('.flightBoth');
    // per leggibilità ho messo in due variabili questi dati
    const components = this.dataToInsert.currentQuote?.components;
    const direction = components?.length > 0 ? components[0].direction : undefined;

    if(direction){
      if(direction === 'OutBound'){
        flightBoth.forEach(el => el.remove());
        flightTo.forEach(el => el.remove());
      }else if(direction === 'InBound'){
        flightBoth.forEach(el => el.remove());
        flightFrom.forEach(el => el.remove());
      } else{
        flightTo.forEach(el => el.remove());
        flightFrom.forEach(el => el.remove());
      }
    } else {
      flightBoth.forEach(el => el.remove());
      flightFrom.forEach(el => el.remove());
      flightTo.forEach(el => el.remove());
    }

    const headerExternalBookingCodeNode = layoutDOM.getElementById('headerExternalBookingCode');
    if (headerExternalBookingCodeNode && !this.getExternalBookingCode()) {
      // verifico se è presente il codice pratica esterna e in tal caso, se NON presente, rimuovo il relativo nodo DOM sull'header dedicato al codice pratica esterna
      headerExternalBookingCodeNode.remove();
    }
    const dinnerInfoNode = layoutDOM.getElementById('dinnerInfo');
    const dinnerInfoRecapNode = layoutDOM.getElementById('dinnerInfoRecap');
    if (dinnerInfoNode && dinnerInfoRecapNode && !this.dataToInsert.currentQuote?.components[0]?.description) {
      // verifico se sono presenti informazioni riguardo il turno cena altrimenti, elimino il nodo DOM della parte del turno cena sulla email
      dinnerInfoNode.remove();
      dinnerInfoRecapNode.remove();
    }
    const departure  = layoutDOM.getElementById('departure');
    const departureFrom = layoutDOM.getElementById('departureFrom');
    const sololand  = layoutDOM.getElementById('sololand');
    if (departure && departureFrom && sololand) {
      if (this.dataToInsert.currentQuote?.packageContent?.departurePlace) {
        departure.style.display = 'block';
        departureFrom.style.display = 'block';
        sololand.style.display = 'none';
      } else {
        departure.style.display = 'none';
        departureFrom.style.display = 'none';
        sololand.style.display = 'block';

      }
    }
    const colSito = layoutDOM.getElementById('colAgencyData__website');
    const colContatti = layoutDOM.getElementById('colAgencyData__tel');
    if (!this.dataToInsert.profileData.sito || this.dataToInsert.profileData.sito.trim().length === 0) {
      colSito.remove();
      // tslint:disable-next-line: deprecation
      (colContatti as HTMLTableElement).align = 'center';
    }
    if (
      (!this.dataToInsert.profileData?.tel1 || this.dataToInsert.profileData.tel1.trim().length === 0) &&
      (!this.dataToInsert.profileData?.tel2 || this.dataToInsert.profileData.tel2.trim().length === 0) &&
      (!this.dataToInsert.profileData?.email1 || this.dataToInsert.profileData.email1.trim().length === 0) &&
      (!this.dataToInsert.profileData?.email2 || this.dataToInsert.profileData.email2.trim().length === 0)
    ) {
      colContatti.remove();
      if (colSito) {
        // tslint:disable-next-line: deprecation
        (colSito as HTMLTableElement).align = 'center';
      }
    }
    if (
      (!this.dataToInsert.profileData.tel1 || this.dataToInsert.profileData.tel1.trim().length === 0) &&
      (!this.dataToInsert.profileData.tel2 || this.dataToInsert.profileData.tel2.trim().length === 0)
    ) {
      layoutDOM.getElementById('colTel')?.remove();
    }
    if (
      (!this.dataToInsert.profileData.email1 || this.dataToInsert.profileData.email1.trim().length === 0) &&
      (!this.dataToInsert.profileData.email2 || this.dataToInsert.profileData.email2.trim().length === 0)
    ) {
      layoutDOM.getElementById('colMail')?.remove();
    }

    const stopOver = layoutDOM.getElementById('stopOver');
    if (stopOver) {
      stopOver.style.display = (this.dataToInsert.currentQuote?.operatives?.[0]?.stopOver) ? 'block' : 'none';
    }
    const arrival = layoutDOM.getElementById('arrival');
    const arrivalPlace  = layoutDOM.getElementById('arrivalPlace');
    if (arrival && arrivalPlace) {
      if (this.dataToInsert.currentQuote?.operatives?.length || this.dataToInsert?.currentQuote?.packageContent?.arrivalPlace?.id) {
        arrival.style.display = 'block';
        arrivalPlace.style.display = 'block';
      } else {
        arrival.style.display = 'none';
        arrivalPlace.style.display = 'none';
      }
    }
  }

  getTopOrAgencyLogo(): string {
    if (this.dataToInsert?.currentQuote?.packageContent?.logo) {
      return this.dataToInsert.currentQuote.packageContent.logo;
    } else {
      if (this.dataToInsert?.masterProfileData?.logo?.trim() && !this.dataToInsert?.profileData?.isMaster && !this.dataToInsert?.profileData?.isAgencySite) {
        // se presente il logo dell'agenzia master e i dati di profilo dell'agenzia corrispondono ad una filiale (NO agenzia master), allora recupero il logo da "masterProfileData"
        this.dataToInsert.profileData.logo = JSON.parse(JSON.stringify(this.dataToInsert.masterProfileData.logo));
      }
      if (!this.dataToInsert?.profileData?.logo?.trim() || !String(this.dataToInsert?.profileData?.logo?.trim()).startsWith('http')) {
        this.dataToInsert.profileData.logo = 'https://cdn.otosrl.com/images/clienti/';
        if (this.agencyNetwork?.networkId === Networks.GEO) {
          this.dataToInsert.profileData.logo += 'logo_geo_otofe_dem.png';
        } else if (this.agencyNetwork?.networkId === Networks.WTG || this.agencyNetwork?.networkId === Networks.WTG_MASTER) {
          this.dataToInsert.profileData.logo += 'logo_wel_otofe_dem.png';
        } else {
          this.dataToInsert.profileData.logo += 'logo_otofe_dem.png';
        }
      } else if (this.dataToInsert?.profileData?.logo?.trim() && String(this.dataToInsert?.profileData?.logo?.trim()).startsWith('http')) {
        return this.dataToInsert.profileData.logo.trim();
      }
      return `https://cdn.otosrl.com/images/clienti/${this.dataToInsert.profileData.logo.trim()}`;
    }
  }

  getAgencyLogo(): string {
    if (this.dataToInsert?.masterProfileData?.logo?.trim() && !this.dataToInsert?.profileData?.isMaster && !this.dataToInsert?.profileData?.isAgencySite) {
      // se presente il logo dell'agenzia master e i dati di profilo dell'agenzia corrispondono ad una filiale (NO agenzia master), allora recupero il logo da "masterProfileData"
      this.dataToInsert.profileData.logo = JSON.parse(JSON.stringify(this.dataToInsert.masterProfileData.logo));
    }
    if (!this.dataToInsert?.profileData?.logo?.trim() || !String(this.dataToInsert?.profileData?.logo?.trim()).startsWith('http')) {
      this.dataToInsert.profileData.logo = 'https://cdn.otosrl.com/images/clienti/';
      if (this.agencyNetwork?.networkId === Networks.GEO) {
        this.dataToInsert.profileData.logo += 'logo_geo_otofe_dem.png';
      } else if (this.agencyNetwork?.networkId === Networks.WTG || this.agencyNetwork?.networkId === Networks.WTG_MASTER) {
        this.dataToInsert.profileData.logo += 'logo_wel_otofe_dem.png';
      } else {
        this.dataToInsert.profileData.logo += 'logo_otofe_dem.png';
      }
    } else if (this.dataToInsert?.profileData?.logo?.trim() && String(this.dataToInsert?.profileData?.logo?.trim()).startsWith('http')) {
      return this.dataToInsert.profileData.logo.trim();
    }
    return `https://cdn.otosrl.com/images/clienti/${this.dataToInsert.profileData.logo.trim()}`;
  }

  insertCharges(layoutDOM: Document) {
    const chargesNode = layoutDOM.getElementById('chargesToRepeat');
    const chargesLocallyNode = layoutDOM.getElementById('chargesLocallyToRepeat');

    const chargesToPay = this.dataToInsert.currentQuote.charges.filter(charge => charge.payableLocally !== 1)
      .sort((a, b) => { return (a.progressive < b.progressive) ? -1 : (a.progressive > b.progressive) ? 1 : 0; });

    const chargesPayableLocally = this.dataToInsert.currentQuote.charges.filter(charge => charge.payableLocally === 1)
      .sort((a, b) => { return (a.progressive < b.progressive) ? -1 : (a.progressive > b.progressive) ? 1 : 0; });


    if (chargesNode) {
      const chargeHTML = chargesNode.innerHTML.replace('id="chargesToRepeat"', '');

      chargesToPay.forEach((charge) => {
        let currentChargeHtml = chargeHTML;

        currentChargeHtml = currentChargeHtml.replace(`[[charge.description]]`, `${charge.description}`);
        currentChargeHtml = currentChargeHtml.replace(`[[charge.value]]`, charge.included === 1 && charge.value === 0 ? 'Gratuito' : this._currencyPipe.transform(charge.value, '€', 'symbol', '1.2-2', 'it'));

        chargesNode.insertAdjacentHTML('beforebegin', currentChargeHtml);
      });

      chargesNode.remove();
    }

    if (chargesLocallyNode) {
      const chargeHTML = chargesLocallyNode.innerHTML.replace('id="chargesLocallyToRepeat"', '');

      if (chargesPayableLocally.length === 0) {
        layoutDOM.getElementById('locallyTable').remove();
      } else {
        chargesPayableLocally.forEach((charge) => {
          let currentChargeHtml = chargeHTML;

          currentChargeHtml = currentChargeHtml.replace(`[[chargeLocal.description]]`, `${charge.description}`);
          currentChargeHtml = currentChargeHtml.replace(`[[chargeLocal.value]]`, charge.included === 1 && charge.value === 0 ? 'Gratuito' : this._currencyPipe.transform(charge.value, '€', 'symbol', '1.2-2', 'it'));

          chargesLocallyNode.insertAdjacentHTML('beforebegin', currentChargeHtml);
        });

        chargesLocallyNode.remove();
      }
    }
  }

  insertCruiseCharges(layoutDOM: Document) {
    const chargesNode = layoutDOM.getElementById('chargesToRepeat');
    let allCharges = [];

    if (this.dataToInsert?.currentQuote?.tourOperator?.id === CruiseTopId.COSTA || this.dataToInsert?.currentQuote?.tourOperator?.id === CruiseTopId.MSC) {
      // se si tratta di cruise Costa o MSC, recupero solo alcuni charges/details che servono da mostrare nel riepilogo in modo formattato secondo la predisposizione della dem
      const portTaxesFromLiveSlim = this.dataToInsert?.currentQuote?.selectedLiveSlim?.details?.filter((detail) => detail.WebCode === 'PCH') || [];
      const insurancesFromLiveSlim = this.dataToInsert?.currentQuote?.selectedLiveSlim?.details?.filter((detail) => detail.WebCode === 'INS') || [];
      const cabinCharge = this.dataToInsert?.currentQuote?.selectedLiveSlim?.details?.find((detail) => (detail.WebCode === 'CAB'));

      allCharges.push({
        description: 'Quota base',
        value: this.dataToInsert?.currentQuote?.selectedLiveSlim?.basePrice || cabinCharge?.GrossPrice || 0
      });
      if (this.dataToInsert?.currentQuote?.tourOperator?.id == CruiseTopId.MSC && this.dataToInsert?.currentQuote?.selectedLiveSlim?.onaPrice) {
        allCharges.push({
          description: 'Tasse',
          value: this.dataToInsert?.currentQuote?.selectedLiveSlim?.onaPrice
        });
      }
      if (portTaxesFromLiveSlim?.length) {
        for (let i = 0; i < portTaxesFromLiveSlim.length; i++) {
          allCharges.push({
            description: portTaxesFromLiveSlim[i].Description,
            value: Number(portTaxesFromLiveSlim[i].GrossPrice)
          });
        }
      }
      if (insurancesFromLiveSlim?.length) {
        for (let i = 0; i < insurancesFromLiveSlim.length; i++) {
          allCharges.push({
            description: insurancesFromLiveSlim[i].Description,
            value: Number(insurancesFromLiveSlim[i].GrossPrice)
          });
        }
      }
    } else {
      // ... altrimenti, se NON è una cruise Costa ed MSC, allora, recupero tutti i charges/details ritornati in modo da mostrarli nel riepilogo
      allCharges = this.dataToInsert?.currentQuote?.selectedLiveSlim?.details?.map((detail) => {
        detail.description = detail.Description;
        detail.value = detail.GrossPrice;
        return detail;
      })?.sort((ch1: any, ch2: any) => ch1.value - ch2.value) || [];
    }

    if (chargesNode) {
      const chargeHTML = chargesNode.innerHTML.replace('id="chargesToRepeat"', '');

      allCharges.forEach((charge: any) => {
        let currentChargeHtml = chargeHTML;

        currentChargeHtml = currentChargeHtml.replace(`[[charge.description]]`, `${charge.description}`);
        currentChargeHtml = currentChargeHtml.replace(`[[charge.value]]`, (charge.value === 0) ? 'Incluso' : this._currencyPipe.transform(charge.value, '€', 'symbol', '1.2-2', 'it'));

        chargesNode.insertAdjacentHTML('afterend', currentChargeHtml);
      });

      chargesNode.remove();
    }
  }

  insertMeetingPoints(layoutDOM: Document) {
    const meetingPointsNode = layoutDOM.getElementById('meetingPointsToRepeat');

    if (meetingPointsNode) {
      const chargeHTML = meetingPointsNode.innerHTML.replace('id="chargesToRepeat"', '');
      if (this.dataToInsert.currentQuote.packageContent.experience.hotelPickup === 0) {
        this.dataToInsert.currentQuote.packageContent.experience.meetingPoints.forEach((meetingPoint) => {
          let currentMeetingPointHtml = chargeHTML;

          currentMeetingPointHtml = currentMeetingPointHtml.replace(`[[meetingPoint.place]]`, `<small>${meetingPoint.destination.name}</small> <br /> ${meetingPoint.destinationName}`);

          meetingPointsNode.insertAdjacentHTML('afterend', currentMeetingPointHtml);
        });
      } else {
        let currentMeetingPointHtml = chargeHTML;
        currentMeetingPointHtml = currentMeetingPointHtml.replace(`[[meetingPoint.place]]`, `Pickup in hotel`);
        meetingPointsNode.insertAdjacentHTML('afterend', currentMeetingPointHtml);
      }

      meetingPointsNode.remove();
    }
  }

  insertRelated(layoutDOM: Document) {
    const chargesNode = layoutDOM.getElementById('relatedToRepeat');

    if (chargesNode) {
      const chargeHTML = chargesNode.innerHTML.replace('id="relatedToRepeat"', '');

      this.dataToInsert.currentQuote?.related?.forEach((related) => {
        let currentRelatedHtml = chargeHTML;

        currentRelatedHtml = currentRelatedHtml.replace(`[[related.description]]`, `${related.packageContent.title}`);
        currentRelatedHtml = currentRelatedHtml.replace(`[[related.value]]`, this._currencyPipe.transform(related.value, '€', 'symbol', '1.2-2', 'it'));

        chargesNode.insertAdjacentHTML('afterend', currentRelatedHtml);
      });

      chargesNode.remove();
    }
  }

  insertAgencyMessage(layoutDOM: Document) {
    const hasOndemand = this.dataToInsert.currentQuote.charges?.filter(t => t.onDemand === 1)?.length > 0;
    if (hasOndemand) {
      const onDemandNode = layoutDOM.getElementById('onDemand');
      if (onDemandNode) {
        onDemandNode.innerHTML = 'Attenzione. La sua pratica contiene dei servizi su richiesta.';
      }
    }
    const agencyMessageNode = layoutDOM.getElementById('agencyMessage');
    if (agencyMessageNode && this.dataToInsert.currentQuote.packageContent.layoutMessage) {
      agencyMessageNode.innerHTML = this.dataToInsert.currentQuote.packageContent.layoutMessage;
    } else {
      layoutDOM.getElementById('agencyMessageBlock')?.remove();
      layoutDOM.getElementById('agencyMessageDivider')?.remove();
    }
  }

  insertTourOrAccommodationDescription(layoutDOM: Document) {
    const textToAppendNode = layoutDOM.getElementById('textToAppend');
    const previewTextToAppendNode = layoutDOM.getElementById('previewTextToAppend');
    const textToAppend = this.getText() || '';
    if (textToAppendNode) {
      textToAppendNode.innerHTML = textToAppend;
    }
    if (previewTextToAppendNode) {
      previewTextToAppendNode.innerHTML = textToAppend;
    }

  }

  insertPassengersData(layoutDOM: Document) {
    const passengerDataBlock = layoutDOM.getElementById('passengerTable');
    const demDividerDashed = layoutDOM.getElementById('demDividerDashed');
    if (passengerDataBlock && demDividerDashed) {
      this.dataToInsert.currentQuote.customer.forEach((customer, customerIndex) => {

        const passengerDataBlockCopy = document.createElement('table');
        // tslint:disable-next-line: no-string-literal
        [...passengerDataBlock.parentElement.children['passengerTable'].attributes].forEach(attribute => {
          passengerDataBlockCopy.setAttribute(attribute.name, attribute.value);
        });
        passengerDataBlockCopy.innerHTML = passengerDataBlock.innerHTML;

        const dividerDashed = document.createElement('table');
        [...passengerDataBlock.parentElement.children['demDividerDashed'].attributes].forEach(attribute => {
          dividerDashed.setAttribute(attribute.name, attribute.value);
        });
        dividerDashed.innerHTML = demDividerDashed.innerHTML;

        const passengerData = {
          first: passengerDataBlockCopy.getElementsByClassName('passengerData__first')[0],
          first_br: passengerDataBlockCopy.getElementsByClassName('passengerData__first-br')[0],
          name: passengerDataBlockCopy.getElementsByClassName('passengerData__name')[0],
          email: passengerDataBlockCopy.getElementsByClassName('passengerData__email')[0],
          birth: passengerDataBlockCopy.getElementsByClassName('passengerData__birth')[0],
          phone: passengerDataBlockCopy.getElementsByClassName('passengerData__phone')[0],
          fiscod: passengerDataBlockCopy.getElementsByClassName('passengerData__fiscod')[0],
          message: passengerDataBlockCopy.getElementsByClassName('passengerData__message')[0],
          sectionTitle: passengerDataBlockCopy.getElementsByClassName('passengerData__sectionTitle')[0]
        };

        passengerData.name.innerHTML = `${this._titlecasePipe.transform(customer.name)} ${this._titlecasePipe.transform(customer.surname)}`;
        passengerData.sectionTitle.className.replace('passengerData__name', '');

        const dateOfBirth = this._datePipe.transform(customer.dateOfBirth, 'shortDate');
        passengerData.birth.innerHTML = `${dateOfBirth} - (${customer.age} anni) ${customer.sex ? '- ' + customer.sex.toUpperCase() : ''}`;
        passengerData.sectionTitle.className.replace('passengerData__birth', '');

        if (customerIndex !== 0) {
          passengerData.sectionTitle.remove();
        } else {
          passengerData.sectionTitle.className.replace('passengerData__sectionTitle', '');
        }
        if (customer.first !== 1) {
          passengerData.first.remove();
          passengerData.first_br.remove();
        }
        if (!customer.email) {
          passengerData.email.remove();
        } else {
          passengerData.email.innerHTML = `Email: <a href="mailto:${customer.email}">${customer.email}</a>`;
          passengerData.sectionTitle.className.replace('passengerData__email', '');
        }
        if (!customer.phoneNumber) {
          passengerData.phone.remove();
        } else {
          passengerData.phone.innerHTML = `Tel: <a href="tel:${customer.phoneNumber}">${customer.phoneNumber}</a>`;
          passengerData.sectionTitle.className.replace('passengerData__phone', '');
        }
        if (!customer.fiscalCode) {
          passengerData.fiscod.remove();
        } else {
          passengerData.fiscod.innerHTML = `Codice Fiscale: ${customer.fiscalCode.toUpperCase()}`;
          passengerData.sectionTitle.className.replace('passengerData__fiscod', '');
        }
        if (!customer.message) {
          passengerData.message.remove();
        } else {
          passengerData.message.innerHTML = `Messaggio dal cliente: <br>${customer.message}`;
          passengerData.sectionTitle.className.replace('passengerData__message', '');
        }

        passengerDataBlockCopy.className.replace('passengerTable', '');
        passengerDataBlockCopy.removeAttribute('id');
        if (customerIndex !== 0) {
          dividerDashed.removeAttribute('id');
          passengerDataBlock.insertAdjacentElement('beforebegin', dividerDashed);
        }
        passengerDataBlock.insertAdjacentElement('beforebegin', passengerDataBlockCopy);

      });

      layoutDOM.getElementById('demDividerDashed').remove();
      passengerDataBlock.remove();
    }
  }

  insertContactInfo(layoutDOM: Document) {
    let contactInfoData = '';
    if (this.dataToInsert.profileData.email_1 && this.dataToInsert.profileData.email_1?.trim().length > 0) {
      contactInfoData += `<a href="mailto:${this.dataToInsert.profileData.email_1}" target="_blank" rel="noopener noreferrer" style="mso-line-height-rule: exactly;-ms-text-size-adjust: 100%;-webkit-text-size-adjust: 100%;font-weight: normal;text-decoration: underline;">
        ${this.dataToInsert.profileData.email_1}
      </a>`;
    }

    if (this.dataToInsert.profileData.email_2 && this.dataToInsert.profileData.email_2?.trim().length > 0) {
      contactInfoData += ((this.dataToInsert.profileData.email_1 && this.dataToInsert.profileData.email_1?.trim().length > 0) ? ', ' : '') + `<a href="mailto:${this.dataToInsert.profileData.email_2}" target="_blank" rel="noopener noreferrer" style="mso-line-height-rule: exactly;-ms-text-size-adjust: 100%;-webkit-text-size-adjust: 100%;font-weight: normal;text-decoration: underline;">
        ${this.dataToInsert.profileData.email_2}
      </a>`;
    }

    if (
      (this.dataToInsert.profileData.tel_1 && this.dataToInsert.profileData.tel_1?.trim().length > 0) ||
      (this.dataToInsert.profileData.tel_2 && this.dataToInsert.profileData.tel_2?.trim().length > 0)
    ) {
      contactInfoData += (this.dataToInsert.profileData.email_1 || this.dataToInsert.profileData.email_1 ? ', ' : '') + 'Tel: ';
      if (this.dataToInsert.profileData.tel_1 && this.dataToInsert.profileData.tel_1?.trim().length > 0) {
        contactInfoData += `<a href="tel:${this.dataToInsert.profileData.tel_1}" style="mso-line-height-rule: exactly;-ms-text-size-adjust: 100%;-webkit-text-size-adjust: 100%;font-weight: normal;text-decoration: underline;">
          ${this.dataToInsert.profileData.tel_1}
        </a>`;
      }
      if (this.dataToInsert.profileData.tel_2 && this.dataToInsert.profileData.tel_2?.trim().length > 0) {
        contactInfoData += ((this.dataToInsert.profileData.tel_1 && this.dataToInsert.profileData.tel_1?.trim().length > 0) ? ', ' : '') + `<a href="tel:${this.dataToInsert.profileData.tel_2}" style="mso-line-height-rule: exactly;-ms-text-size-adjust: 100%;-webkit-text-size-adjust: 100%;font-weight: normal;text-decoration: underline;">
          ${this.dataToInsert.profileData.tel_2}
        </a>`;
      }
    }
    layoutDOM.getElementById('contactInfo').innerHTML = contactInfoData;
  }

  insertExpireDate(layoutDOM: any) {
    if (this.dataToInsert.currentQuote?.expirationDate) {
      const node = layoutDOM.getElementById('expireDate');
      node.innerHTML = 'Data scadenza opzione:' + this._datePipe.transform(this.dataToInsert.currentQuote?.expirationDate, 'dd/MM/yyyy');
    } else {
      layoutDOM.getElementById('expireDate')?.remove();
    }
  }

  getTotalValue() {
    let totalValue = 0;

    this.dataToInsert.currentQuote.charges.forEach(charge => {
      totalValue += charge.payableLocally !== 1 ? Number(charge.value) : 0;
    })

    this.dataToInsert.currentQuote?.related?.forEach(related => {
      totalValue += related.value;
    });
    return this._currencyPipe.transform(totalValue, '€', 'symbol', '1.2-2', 'it');
  }

  getCruiseTotalValue() {
    const totalPriceCharge = this.dataToInsert?.currentQuote?.selectedLiveSlim?.details?.find((detail) => (detail.WebCode === 'TOT'));

    let totalValue = 0;
    if (this.dataToInsert?.currentQuote?.selectedLiveSlim?.consumerPrice) {
      totalValue = this.dataToInsert.currentQuote.selectedLiveSlim.consumerPrice;
    } else if (!!totalPriceCharge) {
      totalValue = totalPriceCharge?.GrossPrice;
    }
    return this._currencyPipe.transform(totalValue, '€', 'symbol', '1.2-2', 'it');
  }

  getTotalLocallyValue() {
    let totalValue = 0;

    this.dataToInsert.currentQuote.charges.forEach(charge => {
      totalValue += charge.payableLocally === 1 ? Number(charge.value) : 0;
    });

    return this._currencyPipe.transform(totalValue, '€', 'symbol', '1.2-2', 'it');
  }

  getTreatment() {
    return this.dataToInsert.currentQuote?.packageContent?.treatment?.name ? this.dataToInsert.currentQuote.packageContent.treatment.name : '' +
      (this.dataToInsert.currentQuote?.packageContent?.treatment?.code ? '(' + this.dataToInsert.currentQuote.packageContent.treatment.code + ')' : '');
  }

  getText() {
    const regexp = /(<br>|<br\/>|<br\ \/>)/gi;
    let textToReturn: string;

    switch (this.dataToInsert.currentQuote?.packageContent?.typology) {
      case 'Holiday':
        textToReturn = this.dataToInsert.currentQuote.packageContent.accommodation.accommodationTexts[0]?.description;
        break;
      case 'Tour':
        textToReturn = this.dataToInsert.currentQuote.packageContent.tour.details[0]?.description;
        break;
      case 'Cruise':
        textToReturn = this.dataToInsert.currentQuote.packageContent.itinerary.parent.name + ' - ' + this.dataToInsert.currentQuote.packageContent.itinerary.name;
        break;
      case 'Experience':
        const text = this.dataToInsert.currentQuote.packageContent.experience.experiencePlan.texts.find(text => text.progressive === 1);
        textToReturn = `<b>${text?.title}:</b> <br /> ${text?.description}`;
        break;
      default:
        textToReturn = null;
    }

    if (textToReturn) {
      const tmpDiv = document.createElement('div');
      textToReturn = textToReturn.replace(regexp, '\n');
      textToReturn = textToReturn.substr(0, 500);
      const idxToDelete = textToReturn.lastIndexOf(' ');
      tmpDiv.innerHTML = textToReturn.substr(0, idxToDelete) + '...';
      tmpDiv.innerHTML = tmpDiv.textContent.replace('\n', '<br />');
      return tmpDiv.innerHTML;
    }
  }

  getRoom(): string {
    let textToReturn: string;
    switch (this.dataToInsert.currentQuote?.packageContent?.typology) {
      case 'Holiday':
        textToReturn = this.dataToInsert.currentQuote?.packageContent?.roomTypology;
        break;
      case 'Tour':
        textToReturn = 'N/D';
        break;

    }
    return textToReturn;
  }

  private formatProfileDataForDem(profileData: Agenzia): IRemoteProfile {
    const profileDataFormatted = JSON.parse(JSON.stringify(profileData));

    profileDataFormatted.ragione_sociale = profileDataFormatted.businessName;
    profileDataFormatted.denominazione = profileDataFormatted.denomination;
    profileDataFormatted.indirizzo = profileDataFormatted.address;
    profileDataFormatted.citta = profileDataFormatted.city;
    profileDataFormatted.provincia = profileDataFormatted.province;
    profileDataFormatted.email_1 = profileDataFormatted.email1;
    profileDataFormatted.email_2 = profileDataFormatted.email2;
    profileDataFormatted.tel_1 = profileDataFormatted.tel1;
    profileDataFormatted.tel_2 = profileDataFormatted.tel2;
    profileDataFormatted.sito = profileDataFormatted.site;

    delete profileDataFormatted.businessName;
    delete profileDataFormatted.denomination;
    delete profileDataFormatted.address;
    delete profileDataFormatted.city;
    delete profileDataFormatted.province;
    delete profileDataFormatted.email1;
    delete profileDataFormatted.email2;
    delete profileDataFormatted.tel1;
    delete profileDataFormatted.tel2;
    delete profileDataFormatted.site;

    return profileDataFormatted;
  }
}

interface TemplateData {
  link: string;
  ogTitle: string;
  ogImg: string;
  ogDescription: string;
  logoLink: string;
  email: string;
  address: string;
  naming: string;
  city: string;
}
